import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useAuthApiStore } from './authApiStore'

interface AuthGuardOptions {
  redirectRoute: string
  authCheckFn?: () => boolean
}

/**
 * Creates a consistent authentication guard for Vue Router
 *
 * @param options Configuration options for the guard
 * @returns A navigation guard function to use with router.beforeEach
 */
export function createAuthGuard(options: AuthGuardOptions) {
  const { redirectRoute, authCheckFn } = options

  // Default auth check uses authApiStore if no custom check provided
  const isAuthenticated =
    authCheckFn ||
    (() => {
      const authStore = useAuthApiStore()
      return authStore.isAuthenticated
    })

  return (
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ) => {
    // Skip auth check if route doesn't require authentication
    if (!to.meta.requiresAuth) {
      return next()
    }

    // Check if user is authenticated
    if (isAuthenticated()) {
      return next()
    }

    // Redirect to login with original destination in query params
    return next({
      name: redirectRoute,
      query: {
        redirectFrom: to.fullPath,
      },
    })
  }
}
